import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import StickyMenu from 'core/modules/Partials/StickyMenu/StickyMenu';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import DynamicMerchant from './components/DynamicMerchant/DynamicMerchant';
import Navigation from './components/Navigation/Navigation';
import List from 'core/modules/Partials/List/List';
import { useGetDynamicMerchants } from './hooks';
import { selectDynamicMerchantsConfig } from './selectors';

import './DynamicMerchants.scss';

const CAROUSEL_VIEW = '4_column_carousel';
const LARGE_LOGO_TREATMENT = 'large';

function DynamicMerchants({
  title, namespace, content_group_id, logo_treatment: logoTreatment, view_type: viewType,
}) {
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const dynamicMerchantsRef = useRef();
  const config = useSelector(selectDynamicMerchantsConfig);
  const [activeSort, setActiveSort] = useState(config.api.params.sort_by);
  const [activeFilter, setActiveFilter] = useState(null);
  const {
    merchants, merchantsToShow, merchantsNumberOnInitialLoad,
  } = useGetDynamicMerchants(namespace, content_group_id, activeSort, activeFilter);
  const {
    checkShouldShowMenu, minMerchantsForSort, carousel, getExtraLabel,
  } = config;

  if (merchantsNumberOnInitialLoad === 0) {
    return null;
  }

  const shouldUseCarousel = viewType === CAROUSEL_VIEW;
  const shouldShowMenu = checkShouldShowMenu(shouldUseCarousel, merchantsNumberOnInitialLoad, minMerchantsForSort);
  const shouldShowOfferInfo = logoTreatment === LARGE_LOGO_TREATMENT;
  const gaViewType = shouldUseCarousel ? 'carousel' : 'grid';
  const logoClassName = `mn_${logoTreatment}`;
  const extraLabel = getExtraLabel(logoTreatment);

  const getMerchantListClassNames = () => {
    const viewClassName = shouldUseCarousel ? 'mn_carouselView' : 'mn_gridView';

    return `mn_dynamicMerchantsList ${logoClassName} ${viewClassName}`;
  };

  return (
    <div
      className="mn_dynamicMerchants"
      ref={dynamicMerchantsRef}
      data-title={title}
      data-merchants-count={merchantsToShow.length}
    >
      <SectionTitle title={title} />
      { shouldShowMenu &&
        <StickyMenu
          bottomOffset="-500px"
          wrapperRef={dynamicMerchantsRef}
        >
          <Navigation
            dynamicMerchantsRef={dynamicMerchantsRef}
            merchants={merchants}
            merchantsToShow={merchantsToShow}
            title={title}
            setActiveSort={setActiveSort}
            activeSort={activeSort}
            setActiveFilter={setActiveFilter}
            activeFilter={activeFilter}
          />
        </StickyMenu>
      }
      <div className={getMerchantListClassNames()}>
        <List options={carousel} shouldUseCarousel={shouldUseCarousel} hasFillers={isMobileMQ ? 1 : 5}>
          {merchantsToShow.map((merchant) => (
            <DynamicMerchant
              placement={merchant}
              key={merchant.id}
              shouldShowOfferInfo={shouldShowOfferInfo}
              logoTreatment={logoTreatment}
              gaViewType={gaViewType}
              customClassName={logoClassName}
              extraLabel={extraLabel}
            />
          ))}
        </List>
      </div>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><DynamicMerchants {...props} /></ComponentErrorBoundary>;
